import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from '@/components/ui/context-menu'
import { Trash2, AlertTriangle, ArrowUp, ArrowDown, Minus } from 'lucide-react'

type Priority = 'no_priority' | 'low' | 'medium' | 'high' | 'urgent'

const priorityConfig: Record<Priority, { icon: React.ReactNode; color: string } | null> = {
  none: null,
  low: { icon: <ArrowDown className="h-3 w-3" />, color: 'text-blue-400' },
  medium: { icon: <Minus className="h-3 w-3" />, color: 'text-yellow-500' },
  high: { icon: <ArrowUp className="h-3 w-3" />, color: 'text-orange-500' },
  urgent: { icon: <AlertTriangle className="h-3 w-3" />, color: 'text-red-500' },
}

interface TaskLabel {
  id: string
  name: string
  color: string
}

interface TaskItem {
  id: string
  title: string
  description: string | null
  position: number
  priority: Priority
  assignee: { id: string; name: string } | null
  labels: TaskLabel[]
  updated_at: string
}

interface Props {
  task: TaskItem
  isDragging?: boolean
  onClick?: () => void
  onDelete?: () => void
}

export default function TaskCard({ task, isDragging, onClick, onDelete }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Wrap dnd-kit's onPointerDown to only activate on left-click,
  // so right-click can trigger the context menu instead
  const patchedListeners = {
    ...listeners,
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button === 2) return
      listeners?.onPointerDown?.(e as any)
    },
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...patchedListeners}
          onClick={onClick}
          className={`rounded-md border bg-white p-3 cursor-grab active:cursor-grabbing transition-colors ${
            isDragging || isSortableDragging
              ? 'border-gray-400 opacity-80 shadow-lg'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5">
            {task.priority && task.priority !== 'no_priority' && priorityConfig[task.priority] && (
              <span className={priorityConfig[task.priority]!.color}>
                {priorityConfig[task.priority]!.icon}
              </span>
            )}
            <p className="text-sm font-medium">{task.title}</p>
          </div>

          {task.labels.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {task.labels.map(label => (
                <Badge
                  key={label.id}
                  variant="outline"
                  className="text-xs px-1.5 py-0"
                  style={{ borderColor: label.color, color: label.color }}
                >
                  {label.name}
                </Badge>
              ))}
            </div>
          )}

          {task.assignee && (
            <div className="flex items-center gap-1.5 mt-2">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-[10px] bg-gray-200">
                  {task.assignee.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-gray-500">{task.assignee.name}</span>
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {onDelete && (
          <ContextMenuItem variant="destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
            Delete task
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}
